import {
  ExternalLink,
  Pencil,
  Plus,
  Save,
  Search,
  Trash2,
  X,
} from "lucide-react";
import { useMemo, useState, type ReactNode } from "react";
import { toast } from "sonner";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  useCreateVendorPage,
  useDeleteVendorPage,
  useUpdateVendorPage,
  useVendorPages,
} from "@/hooks/useVendorPages";
import type {
  VendorPage,
  VendorPageCapabilities,
  VendorPageUpsertRequest,
} from "@/lib/api";
import { cn } from "@/lib/utils";

const PAGE_TYPES = [
  "tool",
  "chat_room",
  "room",
  "alerts_feed",
  "portfolio",
  "knowledge",
  "external_app",
] as const;

const ACCESS_MODES = ["external", "iframe", "scripted_iframe"] as const;

const EMPTY_CAPABILITIES: VendorPageCapabilities = {
  open: true,
  embed: false,
  scrape: false,
  warm_page: false,
  monitor_activity: false,
  index: false,
};

/** Operator CRUD for vendor pages: tools, chat rooms, feeds, and scrape-able pages. */
export default function VendorPagesAdminPage() {
  const pagesQ = useVendorPages();
  const pages = pagesQ.data ?? [];
  const [editing, setEditing] = useState<VendorPage | null>(null);
  const [creating, setCreating] = useState(false);
  const [filter, setFilter] = useState("all");
  const [query, setQuery] = useState("");

  const filteredPages = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return pages.filter((page) => {
      if (filter !== "all" && !matchesFacet(page, filter)) return false;
      if (!needle) return true;
      return [
        page.id,
        page.label,
        page.nav_label,
        page.vendor,
        page.vendor_display_name,
        page.subscription,
        page.section,
        page.page_type,
        page.url,
        page.notes,
      ]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(needle));
    });
  }, [filter, pages, query]);
  const summary = useMemo(() => pageSummary(pages), [pages]);

  return (
    <div className="flex h-full flex-col">
      <header className="border-b border-border px-4 py-4 sm:px-6">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
          <div className="min-w-0">
          <h1 className="text-xl font-semibold">Admin · Vendor pages</h1>
          <p className="mt-1 text-xs text-muted-fg">
            Catalog pages once, then choose whether each page opens, embeds, scrapes, warms, or monitors.
          </p>
            <div className="mt-3 flex flex-wrap gap-1.5">
              <SummaryBadge label="total" value={summary.total} />
              <SummaryBadge label="tools" value={summary.tools} />
              <SummaryBadge label="chats" value={summary.chats} />
              <SummaryBadge label="scrapable" value={summary.scrapable} />
              <SummaryBadge label="embedded" value={summary.embedded} />
              <SummaryBadge label="warm" value={summary.warm} />
            </div>
        </div>
          <div className="flex w-full flex-col gap-2 sm:flex-row lg:w-auto">
            <div className="relative w-full sm:w-64">
              <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-fg" />
              <input
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Search pages"
                className="h-8 w-full rounded-md border border-input bg-bg pl-9 pr-3 text-xs text-fg outline-none focus:ring-1 focus:ring-accent"
              />
            </div>
          <Select value={filter} onValueChange={setFilter}>
              <SelectTrigger className="h-8 w-full text-xs sm:w-44">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
                <SelectItem value="all">All pages</SelectItem>
                <SelectItem value="tools">Tools</SelectItem>
                <SelectItem value="chats">Chat rooms</SelectItem>
                <SelectItem value="scrapable">Scrapable</SelectItem>
                <SelectItem value="embedded">Embedded</SelectItem>
                <SelectItem value="warmable">Warmable</SelectItem>
              {PAGE_TYPES.map((type) => (
                <SelectItem key={type} value={type}>
                  {type}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            size="sm"
              className="justify-center"
            onClick={() => {
              setCreating(true);
              setEditing(null);
            }}
          >
            <Plus />
            New vendor page
          </Button>
        </div>
        </div>
      </header>

      <div className="flex-1 overflow-auto p-4 sm:p-6">
        {pagesQ.isLoading ? (
          <div className="text-sm text-muted-fg">Loading…</div>
        ) : pagesQ.isError ? (
          <div className="text-sm text-red-400">
            {(pagesQ.error as Error).message}
          </div>
        ) : (
          <div className="flex flex-col gap-3">
            {creating ? (
              <VendorPageForm
                initial={null}
                onCancel={() => setCreating(false)}
                onSaved={() => setCreating(false)}
              />
            ) : null}
            {filteredPages.map((page) =>
              editing?.id === page.id ? (
                <VendorPageForm
                  key={page.id}
                  initial={page}
                  onCancel={() => setEditing(null)}
                  onSaved={() => setEditing(null)}
                />
              ) : (
                <VendorPageRow
                  key={page.id}
                  page={page}
                  onEdit={() => {
                    setEditing(page);
                    setCreating(false);
                  }}
                />
              ),
            )}
            {filteredPages.length === 0 ? (
              <div className="rounded-md border border-border bg-muted/5 p-4 text-sm text-muted-fg">
                No vendor pages match this filter.
              </div>
            ) : null}
          </div>
        )}
      </div>
    </div>
  );
}

function VendorPageRow({
  page,
  onEdit,
}: {
  page: VendorPage;
  onEdit: () => void;
}) {
  const del = useDeleteVendorPage();
  const capabilities = capabilityLabels(page.capabilities);
  return (
    <div className="flex flex-col gap-3 rounded-md border border-border bg-muted/5 p-3 sm:flex-row sm:items-start sm:p-4">
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-medium text-fg">{page.nav_label ?? page.label}</span>
          <code className="text-[11px] text-muted-fg">{page.id}</code>
          <Badge variant="outline">{page.page_type}</Badge>
          <Badge variant={page.status === "active" ? "success" : "outline"}>
            {page.status}
          </Badge>
          {capabilities.map((capability) => (
            <Badge key={capability} variant="secondary">
              {capability}
            </Badge>
          ))}
          {visibilityLabels(page).map((label) => (
            <Badge key={label} variant="outline">
              {label}
            </Badge>
          ))}
        </div>
        <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-muted-fg">
          <span>
            vendor: <span className="text-fg">{page.vendor_display_name}</span>
          </span>
          <span>
            subscription: <span className="text-fg">{page.subscription}</span>
          </span>
          <span>
            access: <span className="text-fg">{page.access_mode ?? "external"}</span>
          </span>
          {page.section ? (
            <span>
              section: <span className="text-fg">{page.section}</span>
            </span>
          ) : null}
        </div>
        <a
          href={page.url}
          target="_blank"
          rel="noreferrer"
          className="mt-1 block truncate text-xs text-accent hover:underline"
        >
          {page.url}
        </a>
        {page.notes ? (
          <p className="mt-1 text-xs text-muted-fg">{page.notes}</p>
        ) : null}
      </div>
      <div className="flex shrink-0 flex-wrap items-center gap-2 sm:justify-end">
        <Button size="sm" variant="outline" asChild title="Open vendor page" className="h-8 px-2.5">
          <a href={page.url} target="_blank" rel="noreferrer">
            <ExternalLink />
          </a>
        </Button>
        <Button size="sm" variant="outline" onClick={onEdit} className="h-8 px-2.5">
          <Pencil />
          Edit
        </Button>
        <Button
          size="sm"
          variant="outline"
          onClick={() => {
            if (!confirm(`Delete vendor page '${page.id}'? Scraped inbox content stays in the DB.`)) return;
            del.mutate(page.id, {
              onSuccess: () => toast.success(`deleted ${page.id}`),
              onError: (err) => toast.error(`delete failed: ${err.message}`),
            });
          }}
          disabled={del.isPending}
          className="h-8 px-2.5"
        >
          <Trash2 />
          Delete
        </Button>
      </div>
    </div>
  );
}

interface VendorPageFormProps {
  initial: VendorPage | null;
  onCancel: () => void;
  onSaved: () => void;
}

function VendorPageForm({ initial, onCancel, onSaved }: VendorPageFormProps) {
  const create = useCreateVendorPage();
  const update = useUpdateVendorPage();
  const isEdit = initial !== null;
  const [form, setForm] = useState(() => formFromPage(initial));
  const [capabilities, setCapabilities] = useState<VendorPageCapabilities>(
    initial?.capabilities ?? EMPTY_CAPABILITIES,
  );
  const [configText, setConfigText] = useState(
    JSON.stringify(initial?.config ?? {}, null, 2),
  );

  const set = <K extends keyof VendorPageFormState>(
    key: K,
    value: VendorPageFormState[K],
  ) => setForm((current) => ({ ...current, [key]: value }));

  const submit = () => {
    const config = parseJsonObject(configText);
    if (!config) {
      toast.error("config must be a valid JSON object");
      return;
    }
    const body: VendorPageUpsertRequest = {
      ...form,
      nav_label: blankToNull(form.nav_label),
      vendor_display_name: blankToNull(form.vendor_display_name),
      vendor_base_url: blankToNull(form.vendor_base_url),
      access_mode: blankToNull(form.access_mode),
      iframe_url: blankToNull(form.iframe_url),
      focused_url: blankToNull(form.focused_url),
      focus_url: blankToNull(form.focus_url),
      content_type: blankToNull(form.content_type),
      notes: blankToNull(form.notes),
      capabilities,
      config,
    };

    if (!body.id || !body.label || !body.vendor || !body.subscription || !body.url) {
      toast.error("id, label, vendor, subscription, and URL are required");
      return;
    }

    if (isEdit) {
      update.mutate(
        { id: initial!.id, body },
        {
          onSuccess: () => {
            toast.success(`saved ${body.id}`);
            onSaved();
          },
          onError: (err) => toast.error(`save failed: ${err.message}`),
        },
      );
    } else {
      create.mutate(body, {
        onSuccess: () => {
          toast.success(`created ${body.id}`);
          onSaved();
        },
        onError: (err) => toast.error(`create failed: ${err.message}`),
      });
    }
  };

  const pending = create.isPending || update.isPending;
  return (
    <div className="rounded-md border border-border bg-muted/5 p-4">
      <div className="mb-3 flex items-center justify-between">
        <h3 className="text-sm font-semibold">
          {isEdit ? `Edit ${initial!.id}` : "New vendor page"}
        </h3>
        <Button size="sm" variant="ghost" onClick={onCancel}>
          <X />
        </Button>
      </div>

      <div className="grid grid-cols-2 gap-3 text-xs">
        <Field label="id" required help="stable slug used by routes and sources">
          <input
            value={form.id}
            onChange={(event) => set("id", event.target.value)}
            disabled={isEdit}
            placeholder="stt_iris_screener"
            className={inputClass}
          />
        </Field>
        <Field label="label" required>
          <input
            value={form.label}
            onChange={(event) => set("label", event.target.value)}
            placeholder="STT IRIS Screener"
            className={inputClass}
          />
        </Field>
        <Field label="side nav label">
          <input
            value={form.nav_label}
            onChange={(event) => set("nav_label", event.target.value)}
            placeholder="STT IRIS Screener"
            className={inputClass}
          />
        </Field>
        <Field label="page type">
          <Select value={form.page_type} onValueChange={(value) => set("page_type", value)}>
            <SelectTrigger className="h-8 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {PAGE_TYPES.map((type) => (
                <SelectItem key={type} value={type}>
                  {type}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>

        <Field label="vendor" required>
          <input
            value={form.vendor}
            onChange={(event) => set("vendor", event.target.value)}
            placeholder="stockstotrade"
            className={inputClass}
          />
        </Field>
        <Field label="vendor display">
          <input
            value={form.vendor_display_name}
            onChange={(event) => set("vendor_display_name", event.target.value)}
            placeholder="StocksToTrade"
            className={inputClass}
          />
        </Field>
        <Field label="vendor base URL">
          <input
            value={form.vendor_base_url}
            onChange={(event) => set("vendor_base_url", event.target.value)}
            placeholder="https://university.stockstotrade.com"
            className={inputClass}
          />
        </Field>
        <Field label="subscription" required>
          <input
            value={form.subscription}
            onChange={(event) => set("subscription", event.target.value)}
            placeholder="Elite Stream"
            className={inputClass}
          />
        </Field>
        <Field label="section">
          <input
            value={form.section}
            onChange={(event) => set("section", event.target.value)}
            placeholder="Tools"
            className={inputClass}
          />
        </Field>
        <Field label="status">
          <Select value={form.status} onValueChange={(value) => set("status", value)}>
            <SelectTrigger className="h-8 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="active">active</SelectItem>
              <SelectItem value="inactive">inactive</SelectItem>
              <SelectItem value="candidate">candidate</SelectItem>
            </SelectContent>
          </Select>
        </Field>

        <Field label="URL" required className="col-span-2">
          <input
            value={form.url}
            onChange={(event) => {
              const url = event.target.value;
              set("url", url);
              if (!form.vendor_base_url) set("vendor_base_url", originFromUrl(url) ?? "");
            }}
            placeholder="https://..."
            className={inputClass}
          />
        </Field>

        <Field label="access mode">
          <Select
            value={form.access_mode || "external"}
            onValueChange={(value) => set("access_mode", value)}
          >
            <SelectTrigger className="h-8 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {ACCESS_MODES.map((mode) => (
                <SelectItem key={mode} value={mode}>
                  {mode}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>
        <Field label="content type">
          <input
            value={form.content_type}
            onChange={(event) => set("content_type", event.target.value)}
            placeholder="html | iframe | chat | pdf"
            className={inputClass}
          />
        </Field>
        <Field label="iframe URL" className="col-span-2">
          <input
            value={form.iframe_url}
            onChange={(event) => set("iframe_url", event.target.value)}
            placeholder="Only when access mode is iframe/scripted_iframe"
            className={inputClass}
          />
        </Field>
        <Field label="focused URL" help="small chat/window URL" className="col-span-2">
          <input
            value={form.focused_url}
            onChange={(event) => set("focused_url", event.target.value)}
            placeholder="https://...?...chat_popout=true"
            className={inputClass}
          />
        </Field>
        <Field label="focus URL" help="optional content-only URL" className="col-span-2">
          <input
            value={form.focus_url}
            onChange={(event) => set("focus_url", event.target.value)}
            className={inputClass}
          />
        </Field>

        <Field label="capabilities" className="col-span-2">
          <div className="flex flex-wrap gap-3 rounded-md border border-border bg-bg/40 px-3 py-2">
            {Object.keys(EMPTY_CAPABILITIES).map((key) => (
              <label key={key} className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={capabilities[key as keyof VendorPageCapabilities]}
                  onChange={(event) =>
                    setCapabilities((current) => ({
                      ...current,
                      [key]: event.target.checked,
                    }))
                  }
                />
                <span>{key}</span>
              </label>
            ))}
          </div>
        </Field>

        <Field label="notes" className="col-span-2">
          <textarea
            value={form.notes}
            onChange={(event) => set("notes", event.target.value)}
            rows={2}
            className={cn(inputClass, "h-auto py-1.5")}
          />
        </Field>
        <Field
          label="config JSON"
          className="col-span-2"
          help="scripted iframe selectors, launch params, scrape hints, or page-specific metadata"
        >
          <textarea
            value={configText}
            onChange={(event) => setConfigText(event.target.value)}
            rows={8}
            spellCheck={false}
            className={cn(inputClass, "h-auto py-1.5 font-mono")}
          />
        </Field>
      </div>

      <div className="mt-4 flex justify-end gap-2">
        <Button size="sm" variant="outline" onClick={onCancel} disabled={pending}>
          Cancel
        </Button>
        <Button size="sm" onClick={submit} disabled={pending}>
          <Save />
          {isEdit ? "Save" : "Create"}
        </Button>
      </div>
    </div>
  );
}

interface VendorPageFormState {
  id: string;
  label: string;
  nav_label: string;
  vendor: string;
  vendor_display_name: string;
  vendor_base_url: string;
  subscription: string;
  section: string;
  page_type: string;
  url: string;
  status: string;
  access_mode: string;
  iframe_url: string;
  focused_url: string;
  focus_url: string;
  content_type: string;
  notes: string;
}

function formFromPage(page: VendorPage | null): VendorPageFormState {
  return {
    id: page?.id ?? "",
    label: page?.label ?? "",
    nav_label: page?.nav_label ?? "",
    vendor: page?.vendor ?? "",
    vendor_display_name: page?.vendor_display_name ?? "",
    vendor_base_url: "",
    subscription: page?.subscription ?? "",
    section: page?.section ?? "",
    page_type: page?.page_type ?? "tool",
    url: page?.url ?? "",
    status: page?.status ?? "active",
    access_mode: page?.access_mode ?? "external",
    iframe_url: page?.iframe_url ?? "",
    focused_url: page?.focused_url ?? "",
    focus_url: page?.focus_url ?? "",
    content_type: page?.content_type ?? "",
    notes: page?.notes ?? "",
  };
}

function Field({
  label,
  required,
  help,
  className,
  children,
}: {
  label: string;
  required?: boolean;
  help?: string;
  className?: string;
  children: ReactNode;
}) {
  return (
    <label className={cn("flex flex-col gap-1", className)}>
      <span className="text-[10px] font-medium uppercase tracking-wider text-muted-fg">
        {label}
        {required ? <span className="text-red-400"> *</span> : null}
        {help ? <span className="ml-2 normal-case text-muted-fg/70">{help}</span> : null}
      </span>
      {children}
    </label>
  );
}

function SummaryBadge({ label, value }: { label: string; value: number }) {
  return (
    <Badge variant="secondary" className="gap-1">
      <span>{label}</span>
      <span className="text-fg">{value}</span>
    </Badge>
  );
}

function matchesFacet(page: VendorPage, facet: string): boolean {
  if (facet === "tools") return page.page_type === "tool";
  if (facet === "chats") return ["chat_room", "room"].includes(page.page_type);
  if (facet === "scrapable") return page.capabilities.scrape;
  if (facet === "embedded") return page.access_mode === "iframe" || page.access_mode === "scripted_iframe";
  if (facet === "warmable") return page.capabilities.warm_page;
  return page.page_type === facet;
}

function pageSummary(pages: VendorPage[]) {
  return {
    total: pages.length,
    tools: pages.filter((page) => page.page_type === "tool").length,
    chats: pages.filter((page) => ["chat_room", "room"].includes(page.page_type)).length,
    scrapable: pages.filter((page) => page.capabilities.scrape).length,
    embedded: pages.filter((page) => page.access_mode === "iframe" || page.access_mode === "scripted_iframe").length,
    warm: pages.filter((page) => page.capabilities.warm_page).length,
  };
}

function visibilityLabels(page: VendorPage): string[] {
  const labels: string[] = [];
  if (page.page_type === "tool") labels.push("tools page");
  if (["chat_room", "room"].includes(page.page_type)) labels.push("chats page");
  if (page.capabilities.scrape) labels.push("scraper");
  if (page.access_mode === "iframe" || page.access_mode === "scripted_iframe") labels.push("embedded");
  return labels;
}

function capabilityLabels(capabilities: VendorPageCapabilities): string[] {
  return Object.entries(capabilities)
    .filter(([, enabled]) => enabled)
    .map(([capability]) => capability);
}

function parseJsonObject(value: string): Record<string, unknown> | null {
  try {
    const parsed = JSON.parse(value || "{}") as unknown;
    if (parsed === null || Array.isArray(parsed) || typeof parsed !== "object") {
      return null;
    }
    return parsed as Record<string, unknown>;
  } catch {
    return null;
  }
}

function blankToNull(value: string): string | null {
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

function originFromUrl(value: string): string | undefined {
  try {
    return new URL(value).origin;
  } catch {
    return undefined;
  }
}

const inputClass =
  "h-8 w-full rounded-md border border-border bg-transparent px-2 text-xs shadow-sm placeholder:text-muted-fg focus:outline-none focus:ring-1 focus:ring-accent disabled:opacity-50";
